import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Menu, PenLine, SquarePen, Trash2 } from "lucide-react";
import { api, ApiError, type Account, type ComposeKind, type Draft, type Folder, type MessageDetail, type MessageSummary } from "./api";
import { Sidebar, type View } from "./components/Sidebar";
import { MessageList } from "./components/MessageList";
import { Reader } from "./components/Reader";
import { Composer } from "./components/Composer";
import { RulesPage } from "./components/RulesPage";
import { StatsPage } from "./components/StatsPage";
import { AufgabenPage } from "./components/AufgabenPage";
import { BelegePage } from "./components/BelegePage";
import { SettingsPage } from "./components/SettingsPage";
import { Login } from "./components/Login";
import { SetupWizard } from "./components/SetupWizard";
import { OrdnerLeeren } from "./components/OrdnerLeeren";
import { RegelnAnwenden } from "./components/RegelnAnwenden";
import type { SetupState } from "./api";
import { appBadgeSetzen, connectEvents, erlaubnisHolen, hinweisStand, showLocalNotification } from "./lib/notifications";
import { istEingabe, ueberlagerungOffen } from "./lib/dialog";
import { datumKurz, datumLang } from "./lib/datum";
import { ordnerName } from "./lib/ordner";
import { TabLeiste, type Tab } from "./components/TabLeiste";
import { haptik } from "./lib/mobil";
import { useUpdateVerfuegbar } from "./lib/update";
import { adresseLesen, adresseSchreiben } from "./lib/adresse";
import { toast, fehlerText } from "./components/ui/Toast";
import { bestaetigen } from "./components/ui/Bestaetigen";

const REFRESH_MS = 30_000;
const SEITE = 100;
// Die App öffnet mit dem, was wirklich zählt: Mails, die auf eine Antwort warten.
const START: View = { kind: "smart", id: "antworten", label: "Zu beantworten" };

/** Stabiler Schlüssel einer Ansicht — Objektidentität taugt dafür nicht. */
function ansichtSchluessel(v: View): string {
  switch (v.kind) {
    case "folder": return `f:${v.folderId}`;
    case "smart": return `s:${v.id}`;
    case "category": return `c:${v.category}`;
    case "page": return `p:${v.id}`;
  }
}

/** Wert erst übernehmen, wenn eine Weile nichts mehr geändert wurde. */
function useVerzoegert<T>(wert: T, ms: number): T {
  const [v, setV] = useState(wert);
  useEffect(() => {
    const t = window.setTimeout(() => setV(wert), ms);
    return () => window.clearTimeout(t);
  }, [wert, ms]);
  return v;
}

export function App() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [setupFehler, setSetupFehler] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  // Ansicht und Filter aus der Adresse — nach dem Neuladen bleibt alles, wo es war.
  const [anfang] = useState(adresseLesen);
  const [view, setView] = useState<View>(() => anfang.view ?? START);
  // Die geladene Liste samt der Ansicht, zu der sie gehört. So zeigt ein
  // Ordnerwechsel nicht kurz die Mails des vorigen Ordners an.
  const [liste, setListe] = useState<{ ansicht: string; rows: MessageSummary[] }>({ ansicht: "", rows: [] });
  const [listFehler, setListFehler] = useState<string | null>(null);
  const [mehrDa, setMehrDa] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(() => anfang.mail);
  const [detail, setDetail] = useState<MessageDetail | null>(null);
  const [detailFehler, setDetailFehler] = useState<string | null>(null);
  const [detailVersuch, setDetailVersuch] = useState(0);
  const [suche, setSuche] = useState(() => anfang.suche);
  const query = useVerzoegert(suche.trim(), 250);
  const [loading, setLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [antwortenCount, setAntwortenCount] = useState(0);
  const [draftCount, setDraftCount] = useState(0);
  const [aufgabenCount, setAufgabenCount] = useState(0);
  const [ungelesenCount, setUngelesenCount] = useState(0);
  const [entwurfStand, setEntwurfStand] = useState(0);
  const [composing, setComposing] = useState<{ messageId: number | null; kind: ComposeKind; draftId?: number } | null>(null);
  const [dryRun, setDryRun] = useState(false);
  // In schmalen Fenstern ist immer genau eine Spalte sichtbar: Navigation,
  // Liste oder Leser. In breiten Fenstern spielt das keine Rolle.
  const [navOpen, setNavOpen] = useState(false);
  const [setup, setSetup] = useState<SetupState | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(() => anfang.ungelesen);
  // Unterhaltungen bündeln — die Wahl bleibt im Browser.
  const [unterhaltungen, setUnterhaltungen] = useState(() => {
    try { return localStorage.getItem("maildeck-unterhaltungen") === "1"; } catch { return false; }
  });
  const unterhaltungenUmschalten = (an: boolean) => {
    setUnterhaltungen(an);
    try { localStorage.setItem("maildeck-unterhaltungen", an ? "1" : "0"); } catch { /* privater Modus */ }
  };
  const [absender, setAbsender] = useState<string | null>(() => anfang.von);
  const [fragtErlaubnis, setFragtErlaubnis] = useState(false);
  const [leerenOffen, setLeerenOffen] = useState(false);
  const [einsortierenOffen, setEinsortierenOffen] = useState(false);
  const updateDa = useUpdateVerfuegbar();
  const [updateSpaeter, setUpdateSpaeter] = useState(false);

  const vk = ansichtSchluessel(view);
  // Welche Ansicht samt Filter gerade gilt. Die Suche gehört bewusst nicht
  // dazu: Beim Tippen bleiben die bisherigen Treffer stehen, bis neue da sind.
  const ansicht = `${vk}|${unreadOnly ? 1 : 0}|${absender ?? ""}|${unterhaltungen ? 1 : 0}`;
  const filterSchluessel = `${ansicht}|${query}`;

  // Wie viele Mails die Liste zeigt. Nachladen erhöht das Limit; die
  // regelmäßige Aktualisierung behält es bei, statt auf die erste Seite zu
  // springen. Neue Ansicht oder Suche beginnt wieder mit einer Seite — ohne
  // eigenen Effekt, der sonst einen zweiten Abruf auslösen würde.
  const [limitStand, setLimitStand] = useState({ schluessel: "", n: SEITE });
  const limit = limitStand.schluessel === filterSchluessel ? limitStand.n : SEITE;

  const messages = liste.ansicht === ansicht ? liste.rows : [];
  const listRef = useRef<MessageSummary[]>([]);
  listRef.current = messages;
  const selectedRef = useRef<number | null>(null);
  selectedRef.current = selectedId;
  const detailRef = useRef<MessageDetail | null>(null);
  detailRef.current = detail;

  const abmelden = useCallback(() => {
    setAuthed(false);
    setListe({ ansicht: "", rows: [] });
    setSelectedId(null);
    setDetail(null);
    setComposing(null);
    setSuche("");
    setView(START);
  }, []);

  /** 401 heißt: Sitzung abgelaufen. Alles andere meldet der Aufrufer. */
  const sitzungPruefen = useCallback((err: unknown) => {
    if (err instanceof ApiError && err.status === 401) {
      abmelden();
      return true;
    }
    return false;
  }, [abmelden]);

  // ── Anmeldung ───────────────────────────────────────────────────────────────
  const loadSetup = useCallback(async () => {
    try {
      const state = await api.setupState();
      setSetup(state);
      setSetupFehler(null);
      return state;
    } catch (err) {
      setSetupFehler(fehlerText(err));
      return null;
    }
  }, []);

  const starten = useCallback(async () => {
    const state = await loadSetup();
    if (!state) return;
    if (state.canCreateFirstUser) {
      // Frische Installation: kein Login, direkt in den Assistenten.
      setAuthed(false);
      return;
    }
    api.me().then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, [loadSetup]);

  useEffect(() => { void starten(); }, [starten]);

  // Nach der Anmeldung erneut prüfen — erst dann liefert der Zustand Details.
  useEffect(() => {
    if (authed) void loadSetup();
  }, [authed, loadSetup]);

  const loadShell = useCallback(async () => {
    try {
      const [accs, fs, cfg] = await Promise.all([api.accounts(), api.folders(), api.config()]);
      setAccounts(accs);
      setFolders(fs);
      setDryRun(cfg.dryRun);
    } catch (err) {
      if (!sitzungPruefen(err)) toast.fehler(`Ordner konnten nicht geladen werden: ${fehlerText(err)}`);
    }
  }, [sitzungPruefen]);

  useEffect(() => {
    if (authed) void loadShell();
  }, [authed, loadShell]);

  // Stand in die Adresse schreiben (replaceState, kein neuer Verlaufseintrag).
  useEffect(() => {
    if (!authed) return;
    adresseSchreiben({
      schluessel: vk,
      start: ansichtSchluessel(START),
      ungelesen: unreadOnly,
      von: absender,
      suche: query,
      mail: view.kind === "page" ? null : selectedId,
    });
  }, [authed, vk, view.kind, unreadOnly, absender, query, selectedId]);

  // Ordner aus der Adresse, den es nicht mehr gibt (gelöscht, anderes Konto):
  // zurück zum Start statt einer leeren Liste ohne Titel.
  useEffect(() => {
    if (view.kind === "folder" && folders.length > 0 && !folders.some((f) => f.id === view.folderId)) {
      setView(START);
      setSelectedId(null);
    }
  }, [view, folders]);

  // ── Nachrichten laden ───────────────────────────────────────────────────────
  const params = useMemo(() => {
    const base: Record<string, string | number | boolean | undefined> = { q: query || undefined, limit };
    if (view.kind === "folder") { base.folderId = view.folderId; base.mitUnterordnern = true; }
    if (unterhaltungen) base.unterhaltungen = true;
    if (view.kind === "category") base.category = view.category;
    if (view.kind === "smart") {
      if (view.id === "needsAction") base.needsAction = true;
      if (view.id === "antworten") base.zuBeantworten = true;
      // „Ungelesen" meint die eigene Post: Posteingang samt Kundenordnern, ohne
      // Spam — nicht das Ungelesene in Junk, Newsletter oder Benachrichtigungen.
      if (view.id === "unread") { base.unread = true; base.posteingang = true; }
      if (view.id === "hoch") base.priority = "hoch";
    }
    if (unreadOnly) base.unread = true;
    if (absender) base.from = absender;
    return base;
    // vk statt view: dieselbe Ansicht erneut gewählt ist kein neuer Abruf.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vk, query, unreadOnly, absender, limit, unterhaltungen]);

  const istListe = view.kind !== "page" && !(view.kind === "smart" && view.id === "drafts");

  // Jede Anfrage bekommt eine Nummer; nur die jüngste darf die Liste setzen.
  // Sonst überschreibt eine langsame Antwort zur alten Suche die neue.
  const anfrage = useRef(0);
  const loadMessages = useCallback(async () => {
    if (!istListe) return;
    const nr = ++anfrage.current;
    setLoading(true);
    try {
      const rows = await api.messages(params);
      if (nr !== anfrage.current) return;
      setListe({ ansicht, rows });
      setMehrDa(rows.length >= limit);
      setListFehler(null);
    } catch (err) {
      if (nr !== anfrage.current) return;
      if (!sitzungPruefen(err)) setListFehler(fehlerText(err));
    } finally {
      if (nr === anfrage.current) setLoading(false);
    }
  }, [params, istListe, ansicht, limit, sitzungPruefen]);

  // Zählt auch die Ungelesenen je Ordner neu — sonst bleibt die Zahl links
  // stehen, bis jemand von Hand aktualisiert.
  const loadCounts = useCallback(async () => {
    const [pending, drafts, antworten, aufgaben, ungelesen, ordner] = await Promise.all([
      api.pendingActions().catch(() => null),
      api.drafts().catch(() => null),
      api.messages({ zuBeantworten: true, limit: 500 }).catch(() => null),
      api.aufgabenAnzahl().catch(() => null),
      api.ungelesen().catch(() => null),
      api.folders().catch(() => null),
    ]);
    if (ordner) setFolders(ordner);
    if (ungelesen) { setUngelesenCount(ungelesen.anzahl); appBadgeSetzen(ungelesen.anzahl); }
    if (pending) setPendingCount(pending.length);
    if (antworten) setAntwortenCount(antworten.length);
    if (drafts) setDraftCount(drafts.length);
    if (aufgaben) setAufgabenCount(aufgaben.offen);
  }, []);

  useEffect(() => {
    if (!authed) return;
    void loadMessages();
  }, [authed, loadMessages]);

  useEffect(() => {
    if (authed) void loadCounts();
    // Abgemeldet: keine Zahl am Icon stehen lassen, die niemand mehr aktualisiert.
    else if (authed === false) appBadgeSetzen(0);
  }, [authed, loadCounts]);

  // Immer die aktuelle Fassung — für Timer und Ereignisstrom, die sonst bei
  // jeder Suche neu aufgesetzt würden.
  const aktuell = useRef({ loadMessages, loadCounts });
  aktuell.current = { loadMessages, loadCounts };

  // Der Worker meldet neue Mails über den Ereignisstrom; der Timer ist nur
  // Rückfallebene, falls die Verbindung hängt. Im Hintergrund pausiert er und
  // holt beim Zurückkehren sofort nach.
  useEffect(() => {
    if (!authed) return;
    const tick = () => {
      if (document.hidden) return;
      void aktuell.current.loadMessages();
      void aktuell.current.loadCounts();
    };
    const t = window.setInterval(tick, REFRESH_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(t);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    // Mehrere Ereignisse kurz hintereinander lösen nur einen Abruf aus.
    let bald: number | undefined;
    const nachladen = () => {
      window.clearTimeout(bald);
      bald = window.setTimeout(() => {
        void aktuell.current.loadMessages();
        void aktuell.current.loadCounts();
      }, 400);
    };
    const trennen = connectEvents((e) => {
      if (e.type === "eingeordnet" || e.type === "sync") return nachladen();
      if (e.type === "aufgabe") {
        void aktuell.current.loadCounts();
        toast.info("Neuer Kunde erkannt — Ordner anlegen?", { dauer: 8000 });
        return;
      }
      if (e.type !== "neue-mail") return;
      const count = e.payload.count ?? 1;
      const first = e.payload.notable?.[0];
      nachladen();

      toast.info(count === 1 && first ? `Neu von ${first.from}` : `${count} neue Nachrichten`, { dauer: 6000 });

      if (first) {
        showLocalNotification(
          count === 1 ? first.from : `${count} neue Mails`,
          count === 1 ? first.subject : `${first.from}: ${first.subject}`,
        );
        // Einmalig anbieten, wenn nie entschieden wurde — ungefragt nachzufragen
        // ist aufdringlich, gar nicht zu fragen aber auch keine Lösung.
        let gefragt = false;
        try { gefragt = Boolean(sessionStorage.getItem("hinweis-gefragt")); } catch { /* privater Modus */ }
        if (hinweisStand() === "offen" && !gefragt) setFragtErlaubnis(true);
      }
    });
    return () => {
      window.clearTimeout(bald);
      trennen();
    };
  }, [authed]);

  // ── Gewählte Nachricht ──────────────────────────────────────────────────────
  useEffect(() => {
    setDetailFehler(null);
    if (selectedId === null) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    api
      .message(selectedId)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((err) => {
        if (cancelled || sitzungPruefen(err)) return;
        setDetail(null);
        setDetailFehler(err instanceof ApiError && err.status === 404 ? "Die Nachricht gibt es nicht mehr — vermutlich verschoben oder gelöscht." : fehlerText(err));
      });
    return () => { cancelled = true; };
  }, [selectedId, detailVersuch, sitzungPruefen]);

  // ── Zurück-Taste auf dem Telefon ────────────────────────────────────────────
  // Jede offene Ebene (Leser, Navigation) bekommt einen Verlaufseintrag. Die
  // Zurück-Taste schließt dann die oberste Ebene, statt die App zu verlassen.
  const ebenen = useRef({ nav: false, leser: false, tiefe: 0, ignorieren: 0 });
  ebenen.current.nav = navOpen;
  ebenen.current.leser = selectedId !== null;
  useEffect(() => {
    const e = ebenen.current;
    const schmal = window.matchMedia("(max-width: 900px)").matches;
    const ziel = schmal ? Number(navOpen) + Number(selectedId !== null) : 0;
    while (e.tiefe < ziel) {
      e.tiefe++;
      window.history.pushState({ maildeck: e.tiefe }, "");
    }
    if (e.tiefe > ziel) {
      // In der Oberfläche geschlossen: die überzähligen Einträge wieder abbauen,
      // sonst bräuchte die Zurück-Taste später einen Leerlauf-Druck.
      const n = e.tiefe - ziel;
      e.tiefe = ziel;
      e.ignorieren++;
      window.history.go(-n);
    }
  }, [navOpen, selectedId]);
  useEffect(() => {
    const onPop = (ev: PopStateEvent) => {
      const e = ebenen.current;
      if (e.ignorieren > 0) { e.ignorieren--; return; }
      const t = (ev.state as { maildeck?: number } | null)?.maildeck ?? 0;
      let zuSchliessen = e.tiefe - t;
      e.tiefe = t;
      if (zuSchliessen > 0 && e.nav) { setNavOpen(false); zuSchliessen--; }
      if (zuSchliessen > 0 && e.leser) setSelectedId(null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  /**
   * Gelesen-Status sofort überall zeigen — Leser, Liste, Ordnerzahl links und
   * Zähler unten. Der Server (IMAP) braucht oft eine Sekunde; so lange soll
   * die Zahl nicht falsch stehen bleiben.
   */
  const gelesenLokal = useCallback((ids: number[], gelesen: boolean) => {
    const seen = gelesen ? 1 : 0;
    const bekannt = new Map<number, MessageSummary>();
    for (const m of listRef.current) bekannt.set(m.id, m);
    if (detailRef.current) bekannt.set(detailRef.current.id, detailRef.current);
    const delta = new Map<number, number>();
    for (const id of ids) {
      const m = bekannt.get(id);
      if (!m || Boolean(m.seen) === gelesen) continue;
      delta.set(m.folder_id, (delta.get(m.folder_id) ?? 0) + (gelesen ? -1 : 1));
    }
    const menge = new Set(ids);
    setDetail((d) => (d && menge.has(d.id) ? { ...d, seen } : d));
    setListe((alt) => ({ ...alt, rows: alt.rows.map((m) => (menge.has(m.id) ? { ...m, seen } : m)) }));
    if (delta.size > 0) {
      setFolders((fs) => fs.map((f) => (delta.has(f.id) ? { ...f, unseen: Math.max(0, f.unseen + delta.get(f.id)!) } : f)));
    }
  }, []);

  const gelesenSetzen = useCallback(async (id: number, gelesen: boolean) => {
    gelesenLokal([id], gelesen);
    try {
      await api.messageAction(id, gelesen ? "markRead" : "markUnread");
    } finally {
      // Auch bei Fehlern: den echten Stand vom Server holen.
      void aktuell.current.loadCounts();
    }
  }, [gelesenLokal]);

  // Wer eine ungelesene Mail anderthalb Sekunden offen hat, hat sie gelesen —
  // so wie in jedem Mailprogramm. Wer nur mit J/K durchblättert, markiert nichts.
  useEffect(() => {
    if (!detail || detail.seen) return;
    const id = detail.id;
    const t = window.setTimeout(() => {
      if (selectedRef.current !== id) return;
      gelesenSetzen(id, true).catch(() => {
        // Nicht kritisch — beim nächsten Öffnen erneut. Liste wieder ehrlich machen.
        void aktuell.current.loadMessages();
      });
    }, 1500);
    return () => window.clearTimeout(t);
  }, [detail, gelesenSetzen]);

  const refreshAll = useCallback(async () => {
    const id = selectedRef.current;
    await Promise.all([
      aktuell.current.loadMessages(),
      aktuell.current.loadCounts(),
      loadShell(),
      id !== null
        ? api.message(id).then(
            (fresh) => { if (selectedRef.current === id) setDetail(fresh); },
            (err) => {
              if (selectedRef.current !== id || sitzungPruefen(err)) return;
              if (err instanceof ApiError && err.status === 404) setSelectedId(null);
            },
          )
        : Promise.resolve(),
    ]);
    setEntwurfStand((n) => n + 1);
  }, [loadShell, sitzungPruefen]);

  /** Nach Verschieben oder Löschen aus dem Leser: zur nächsten Nachricht weiterrücken. */
  const nachEntfernen = useCallback((id: number) => {
    const l = listRef.current;
    const i = l.findIndex((m) => m.id === id);
    const naechste = i >= 0 ? (l[i + 1] ?? l[i - 1]) : undefined;
    // Sofort aus der Liste nehmen, damit sie nicht bis zum Abruf nachhängt.
    setListe((alt) => ({ ...alt, rows: alt.rows.filter((m) => m.id !== id) }));
    setSelectedId(naechste?.id ?? null);
    void refreshAll();
  }, [refreshAll]);

  /**
   * „Erledigt": braucht keine Antwort — verlässt „Zu beantworten". In dieser
   * Ansicht rückt der Leser gleich zur nächsten Mail weiter.
   */
  const erledigen = useCallback(async (ids: number[]) => {
    try {
      await api.erledigt(ids);
    } catch (err) {
      toast.fehler(err);
      return;
    }
    toast.erfolg(ids.length === 1 ? "Erledigt — keine Antwort nötig" : `${ids.length} als erledigt markiert`, {
      aktion: { label: "Rückgängig", onClick: async () => { await api.erledigt(ids, false); void refreshAll(); } },
    });
    if (vk === "s:antworten") {
      const sel = selectedRef.current;
      if (sel !== null && ids.includes(sel)) return nachEntfernen(sel);
      setListe((alt) => ({ ...alt, rows: alt.rows.filter((m) => !ids.includes(m.id)) }));
    }
    void refreshAll();
  }, [vk, nachEntfernen, refreshAll]);

  const ansichtWaehlen = useCallback((v: View) => {
    setView(v);
    setSelectedId(null);
    setSuche("");
    setAbsender(null);
    setListFehler(null);
    setNavOpen(false);
  }, []);

  // ── Tastatur ────────────────────────────────────────────────────────────────
  const move = useCallback((delta: number) => {
    const l = listRef.current;
    if (l.length === 0) return;
    const idx = l.findIndex((m) => m.id === selectedRef.current);
    const next = idx === -1 ? 0 : Math.min(l.length - 1, Math.max(0, idx + delta));
    setSelectedId(l[next]?.id ?? null);
  }, []);

  const vorschlagEntscheiden = useCallback(async (annehmen: boolean) => {
    const pending = detail?.actions.find((x) => x.status === "pending");
    if (!pending) return false;
    try {
      if (annehmen) {
        await api.approve(pending.id);
        toast.erfolg("Vorschlag übernommen", {
          aktion: { label: "Rückgängig", onClick: async () => { await api.undo(pending.id); void refreshAll(); } },
        });
      } else {
        await api.reject(pending.id);
        toast("Vorschlag verworfen");
      }
    } catch (err) {
      toast.fehler(err);
    }
    void refreshAll();
    return true;
  }, [detail, refreshAll]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Nie mit Strg/Cmd/Alt — sonst schluckt „c" das Kopieren und „r" das Neuladen.
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if (composing || istEingabe(e.target) || ueberlagerungOffen()) return;
      const taste = e.key.length === 1 ? e.key.toLowerCase() : e.key;

      if (taste === "c") { e.preventDefault(); setComposing({ messageId: null, kind: "neu" }); return; }
      if (!istListe) return;

      switch (taste) {
        case "j": e.preventDefault(); move(1); break;
        case "k": e.preventDefault(); move(-1); break;
        case "/": {
          const feld = document.querySelector<HTMLInputElement>(".pane.list .search");
          if (feld) { e.preventDefault(); feld.focus(); feld.select(); }
          break;
        }
        case "r":
          if (selectedId) { e.preventDefault(); setComposing({ messageId: selectedId, kind: "antwort", draftId: detail?.draft_id ?? undefined }); }
          break;
        case "f":
          if (selectedId) { e.preventDefault(); setComposing({ messageId: selectedId, kind: "weiterleiten" }); }
          break;
        case "a":
          if (detail?.id === selectedId) { e.preventDefault(); void vorschlagEntscheiden(true); }
          break;
        case "e":
          if (selectedId) { e.preventDefault(); void erledigen([selectedId]); }
          break;
        case "x":
          if (detail?.id === selectedId) { e.preventDefault(); void vorschlagEntscheiden(false); }
          break;
        case "u":
          if (selectedId && detail?.id === selectedId) {
            e.preventDefault();
            const warGelesen = Boolean(detail.seen);
            gelesenSetzen(selectedId, !warGelesen)
              .then(() => toast(warGelesen ? "Als ungelesen markiert" : "Als gelesen markiert"))
              .catch((err) => { toast.fehler(err); void refreshAll(); });
          }
          break;
        case "Escape":
          if (navOpen) { setNavOpen(false); break; }
          if (selectedId !== null) setSelectedId(null);
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move, selectedId, detail, composing, refreshAll, istListe, navOpen, vorschlagEntscheiden, gelesenSetzen, erledigen]);

  // ── Darstellung ─────────────────────────────────────────────────────────────
  if (setupFehler && !setup) {
    return (
      <div className="empty start-fehler" role="alert">
        <strong>Server nicht erreichbar</strong>
        <span>{setupFehler}</span>
        <button type="button" className="btn" style={{ marginTop: 12 }} onClick={() => { setSetupFehler(null); void starten(); }}>
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (authed === null || setup === null) {
    return <div className="empty start-laden" aria-busy="true"><span className="kreisel" aria-hidden="true" />Einen Moment …</div>;
  }

  // Frische Installation: der Assistent legt den Zugang selbst an.
  if (setup.canCreateFirstUser) {
    return (
      <SetupWizard
        state={setup}
        onDone={async () => { await loadSetup(); setAuthed(true); }}
      />
    );
  }

  if (!authed) return <Login onDone={async () => { setAuthed(true); await loadSetup(); }} />;

  // Angemeldet, aber noch kein Postfach oder kein Modellzugang.
  const needsSetup = !setup.done && (setup.accountCount === 0 || !setup.llm.configured);
  if (needsSetup && !wizardOpen) {
    return <SetupWizard state={setup} onDone={() => void loadSetup()} />;
  }

  // Ordnernamen immer aus den aktuellen Daten — nach dem Umbenennen stimmt
  // das beim Wählen gemerkte Label nicht mehr.
  const aktuellerOrdner = view.kind === "folder" ? folders.find((f) => f.id === view.folderId) : undefined;
  const title =
    aktuellerOrdner ? ordnerName(aktuellerOrdner)
    : view.kind === "folder" || view.kind === "smart" || view.kind === "category" ? view.label : "";

  // Nur Unerwünscht und Papierkorb lassen sich auf einmal leeren.
  const leerbar = aktuellerOrdner && (aktuellerOrdner.special_use === "\\Junk" || aktuellerOrdner.special_use === "\\Trash")
    ? aktuellerOrdner
    : undefined;

  const seitenAnsicht = !istListe;

  // Telefon: untere Leiste. Der Posteingang ist der des ersten Kontos.
  const posteingang = folders.find((f) => f.special_use === "\\Inbox") ?? folders.find((f) => f.path.toUpperCase() === "INBOX");
  const aktiverTab: Tab =
    vk === "s:antworten" ? "antworten"
    : vk === "s:needsAction" ? "vorschlaege"
    : posteingang && vk === `f:${posteingang.id}` ? "posteingang"
    : "mehr";
  const tabWaehlen = (t: Tab) => {
    if (t === "mehr") return setNavOpen(true);
    if (t === aktiverTab) {
      // Zweites Antippen: nach oben, wie in jeder App.
      document.querySelector(".pane.list .pane-scroll")?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (t === "antworten") ansichtWaehlen(START);
    else if (t === "vorschlaege") ansichtWaehlen({ kind: "smart", id: "needsAction", label: "Vorschläge" });
    else if (posteingang) ansichtWaehlen({ kind: "folder", folderId: posteingang.id, label: "Posteingang" });
  };
  // Leser, Navigation und Verfassen brauchen den ganzen Bildschirm.
  const leisteSichtbar = !navOpen && selectedId === null && !composing;

  return (
    <div className="app" data-dryrun={dryRun} data-leiste={leisteSichtbar}>
      {dryRun && (
        <div className="banner" role="status">
          <span>
            <span className="nur-schmal">Testbetrieb — der Agent schlägt nur vor.</span>
            <span className="nur-breit">Testbetrieb: Der Agent schlägt nur vor und verschiebt nichts von selbst. Was du selbst anklickst, wird ausgeführt.</span>
            <span className="nur-breit"> Zum Scharfschalten <code>DRY_RUN=false</code> in der .env setzen und den Worker neu starten.</span>
          </span>
        </div>
      )}
      <Sidebar
        accounts={accounts}
        folders={folders}
        view={view}
        pendingCount={pendingCount}
        antwortenCount={antwortenCount}
        draftCount={draftCount}
        aufgabenCount={aufgabenCount}
        ungelesenCount={ungelesenCount}
        active={navOpen}
        onSelect={ansichtWaehlen}
        onClose={() => setNavOpen(false)}
        onLogout={async () => {
          try { await api.logout(); } catch { /* Sitzung ist ohnehin weg */ }
          abmelden();
        }}
        onFoldersChanged={() => void refreshAll()}
        onCompose={() => setComposing({ messageId: null, kind: "neu" })}
      />

      {seitenAnsicht ? (
        // Ganzseitige Ansichten bekommen in schmalen Fenstern einen eigenen
        // Knopf zur Navigation — die Seiten selbst kennen keinen.
        <div className="seiten-rahmen" data-active={!navOpen}>
          <button type="button" className="icon-btn seiten-nav only-narrow" onClick={() => setNavOpen(true)} aria-label="Navigation öffnen" title="Navigation">
            <Menu aria-hidden="true" />
          </button>
          {view.kind === "page" ? (
            view.id === "rules" ? <RulesPage folders={folders} onFoldersChanged={() => void loadShell()} />
            : view.id === "belege" ? <BelegePage />
            : view.id === "aufgaben" ? <AufgabenPage folders={folders} onChanged={() => void refreshAll()} />
            : view.id === "einrichtung" ? <SettingsPage onOpenWizard={() => setWizardOpen(true)} folders={folders} />
            : <StatsPage />
          ) : (
            <DraftList
              stand={entwurfStand}
              onOpen={(messageId, draftId, kind) => setComposing({ messageId, kind, draftId })}
              onChanged={() => void loadCounts()}
            />
          )}
        </div>
      ) : (
        <>
          <MessageList
            messages={messages}
            selectedId={selectedId}
            loading={loading || liste.ansicht !== ansicht}
            fehler={listFehler}
            query={suche}
            title={title}
            active={!navOpen && selectedId === null}
            unreadOnly={unreadOnly}
            unterhaltungen={unterhaltungen}
            onUnterhaltungen={unterhaltungenUmschalten}
            onUnreadOnly={setUnreadOnly}
            folders={folders}
            onBulkDone={() => void refreshAll()}
            onGelesenLokal={gelesenLokal}
            onErledigt={(ids) => void erledigen(ids)}
            absender={absender}
            onAbsender={setAbsender}
            onReply={(messageId, kind) => setComposing({ messageId, kind })}
            onLeeren={leerbar ? () => setLeerenOffen(true) : undefined}
            onNeuEinsortieren={() => setEinsortierenOffen(true)}
            mehrDa={mehrDa}
            onMehr={() => setLimitStand({ schluessel: filterSchluessel, n: Math.min(limit + SEITE * 2, 5000) })}
            ordner={aktuellerOrdner}
            onFoldersChanged={() => void refreshAll()}
            onOrdnerGeloescht={() => ansichtWaehlen(START)}
            onQuery={setSuche}
            onSelect={setSelectedId}
            onRefresh={() => void refreshAll()}
            onOpenNav={() => setNavOpen(true)}
          />
          <Reader
            message={detail && detail.id === selectedId ? detail : null}
            laedt={selectedId !== null && detail?.id !== selectedId && !detailFehler}
            fehler={selectedId !== null ? detailFehler : null}
            onRetry={() => setDetailVersuch((n) => n + 1)}
            folders={folders}
            active={!navOpen && selectedId !== null}
            onChanged={() => void refreshAll()}
            onGelesen={gelesenSetzen}
            onErledigt={(id) => void erledigen([id])}
            onEntfernt={nachEntfernen}
            onOpen={setSelectedId}
            onReply={(messageId, kind, draftId) => setComposing({ messageId, kind, draftId })}
            onBack={() => setSelectedId(null)}
          />
        </>
      )}

      {leisteSichtbar && (
        <>
          {istListe && (
            <button
              type="button"
              className="fab"
              onClick={() => { haptik(); setComposing({ messageId: null, kind: "neu" }); }}
              aria-label="Neue Mail"
            >
              <SquarePen aria-hidden="true" />
            </button>
          )}
          <TabLeiste
            aktiv={aktiverTab}
            antwortenCount={antwortenCount}
            ungelesenCount={ungelesenCount}
            vorschlaegeCount={pendingCount}
            onWahl={tabWaehlen}
          />
        </>
      )}

      {updateDa && !updateSpaeter && (
        <div className="erlaubnis-hinweis update-hinweis" role="status">
          <span>
            <b>Maildeck wurde aktualisiert.</b>{" "}
            {composing ? "Speichere zuerst deinen Entwurf, dann neu laden." : "Neu laden, um die neue Version zu nutzen."}
          </span>
          <button type="button" className="btn primary" onClick={() => window.location.reload()}>
            Jetzt aktualisieren
          </button>
          <button type="button" className="btn quiet" onClick={() => setUpdateSpaeter(true)}>
            Später
          </button>
        </div>
      )}

      {fragtErlaubnis && (
        <div className="erlaubnis-hinweis" role="region" aria-label="Systemhinweise">
          <span>Sollen neue Mails auch als Hinweis im System erscheinen, wenn das Fenster im Hintergrund liegt?</span>
          <button
            type="button"
            className="btn primary"
            onClick={async () => {
              const stand = await erlaubnisHolen();
              try { sessionStorage.setItem("hinweis-gefragt", "1"); } catch { /* privater Modus */ }
              setFragtErlaubnis(false);
              if (stand === "erlaubt") toast.erfolg("Systemhinweise sind eingeschaltet");
              else if (stand === "verweigert") toast("Abgelehnt — das lässt sich in den Browser-Einstellungen ändern.");
            }}
          >
            Ja, einschalten
          </button>
          <button
            type="button"
            className="btn quiet"
            onClick={() => {
              try { sessionStorage.setItem("hinweis-gefragt", "1"); } catch { /* privater Modus */ }
              setFragtErlaubnis(false);
            }}
          >
            Später
          </button>
        </div>
      )}

      {wizardOpen && (
        <SetupWizard
          state={setup}
          embedded
          onClose={() => setWizardOpen(false)}
          onDone={async () => { setWizardOpen(false); await loadSetup(); await refreshAll(); }}
        />
      )}

      {einsortierenOffen && (
        view.kind === "folder" && folders.some((f) => f.id === view.folderId) ? (
          <RegelnAnwenden
            folders={folders}
            ordner={folders.find((f) => f.id === view.folderId)}
            onClose={() => setEinsortierenOffen(false)}
            onDone={() => { setSelectedId(null); void refreshAll(); }}
          />
        ) : (
          // Ansichten wie „Ungelesen" oder „Zu beantworten": Posteingang samt Unterordnern.
          <RegelnAnwenden
            folders={folders}
            ordnerMenge={{
              titel: view.kind === "page" ? "Posteingang" : `${view.label} · Posteingang samt Unterordnern`,
              ids: folders
                .filter((f) => f.special_use === "\\Inbox" || /^INBOX($|[./])/i.test(f.path))
                .map((f) => f.id),
            }}
            onClose={() => setEinsortierenOffen(false)}
            onDone={() => { setSelectedId(null); void refreshAll(); }}
          />
        )
      )}

      {leerenOffen && leerbar && (
        <OrdnerLeeren
          folder={leerbar}
          label={title}
          onClose={() => setLeerenOffen(false)}
          onDone={() => { setSelectedId(null); void refreshAll(); }}
        />
      )}

      {composing && (
        <Composer
          messageId={composing.messageId}
          draftId={composing.draftId}
          kind={composing.kind}
          onClose={() => { setComposing(null); void loadCounts(); setEntwurfStand((n) => n + 1); }}
          onSent={() => { setComposing(null); void refreshAll(); }}
        />
      )}
    </div>
  );
}

function empfaengerVon(d: Draft): string[] {
  try {
    const v = JSON.parse(d.to_json) as unknown;
    return Array.isArray(v) ? v.map(String) : [];
  } catch {
    return [];
  }
}

function DraftList({ stand, onOpen, onChanged }: {
  /** Ändert sich nach jedem Schließen des Composers — dann neu laden. */
  stand: number;
  onOpen: (messageId: number | null, draftId: number, kind: ComposeKind) => void;
  onChanged: () => void;
}) {
  const [drafts, setDrafts] = useState<Draft[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  // Mehrfachauswahl wie in der Mailliste: Kästchen, Umschalt-Klick für Bereiche.
  const [gewaehlt, setGewaehlt] = useState<Set<number>>(new Set());
  const anker = useRef<number | null>(null);
  const [loescht, setLoescht] = useState(false);

  const laden = useCallback(() => {
    api.drafts()
      .then((d) => { setDrafts(d); setFehler(null); })
      .catch((err) => setFehler(fehlerText(err)));
  }, []);

  useEffect(() => { laden(); }, [laden, stand]);
  // Nach dem Neuladen nur behalten, was es noch gibt.
  useEffect(() => {
    if (!drafts) return;
    setGewaehlt((alt) => new Set([...alt].filter((id) => drafts.some((d) => d.id === id))));
  }, [drafts]);

  const umschalten = (id: number, bereich: boolean) => {
    setGewaehlt((alt) => {
      const neu = new Set(alt);
      const an = !alt.has(id);
      if (bereich && anker.current !== null && drafts) {
        const ids = drafts.map((d) => d.id);
        const i = ids.indexOf(anker.current), j = ids.indexOf(id);
        if (i >= 0 && j >= 0) {
          for (const x of ids.slice(Math.min(i, j), Math.max(i, j) + 1)) { if (an) neu.add(x); else neu.delete(x); }
        }
      } else if (an) neu.add(id);
      else neu.delete(id);
      return neu;
    });
    anker.current = id;
  };

  const alleGewaehlt = Boolean(drafts?.length) && gewaehlt.size === drafts!.length;

  const auswahlLoeschen = async () => {
    const ids = [...gewaehlt];
    if (ids.length === 0) return;
    const ok = await bestaetigen({
      titel: ids.length === 1 ? "Entwurf löschen?" : `${ids.length} Entwürfe löschen?`,
      text: "Die ausgewählten Entwürfe werden endgültig gelöscht. Gesendet wird nichts.",
      bestaetigen: ids.length === 1 ? "Löschen" : `${ids.length} löschen`,
      gefahr: true,
    });
    if (!ok) return;
    setLoescht(true);
    const fehlgeschlagen: number[] = [];
    // In kleinen Paketen, damit der Server nicht fünfzig Anfragen auf einmal sieht.
    for (let i = 0; i < ids.length; i += 8) {
      const paket = ids.slice(i, i + 8);
      const ergebnisse = await Promise.allSettled(paket.map((id) => api.deleteDraft(id)));
      ergebnisse.forEach((r, j) => { if (r.status === "rejected") fehlgeschlagen.push(paket[j]!); });
    }
    setLoescht(false);
    const geloescht = ids.filter((id) => !fehlgeschlagen.includes(id));
    setDrafts((liste) => liste?.filter((d) => !geloescht.includes(d.id)) ?? null);
    setGewaehlt(new Set(fehlgeschlagen));
    if (geloescht.length) toast.erfolg(geloescht.length === 1 ? "Entwurf gelöscht" : `${geloescht.length} Entwürfe gelöscht`);
    if (fehlgeschlagen.length) toast.fehler(`${fehlgeschlagen.length} ließen sich nicht löschen — sie bleiben ausgewählt.`);
    onChanged();
  };

  const loeschen = async (d: Draft) => {
    const ok = await bestaetigen({
      titel: "Entwurf löschen?",
      text: d.subject ? `„${d.subject}" wird endgültig gelöscht.` : "Der Entwurf wird endgültig gelöscht.",
      bestaetigen: "Löschen",
      gefahr: true,
    });
    if (!ok) return;
    try {
      await api.deleteDraft(d.id);
      setDrafts((liste) => liste?.filter((x) => x.id !== d.id) ?? null);
      toast("Entwurf gelöscht");
      onChanged();
    } catch (err) {
      toast.fehler(err);
    }
  };

  return (
    <section className="pane wide" data-active="true" aria-label="Entwürfe">
      <header className="pane-head">
        {drafts && drafts.length > 0 && (
          <input
            type="checkbox"
            className="entwurf-alle"
            checked={alleGewaehlt}
            ref={(el) => { if (el) el.indeterminate = gewaehlt.size > 0 && !alleGewaehlt; }}
            onChange={() => setGewaehlt(alleGewaehlt ? new Set() : new Set(drafts.map((d) => d.id)))}
            aria-label={alleGewaehlt ? "Auswahl aufheben" : "Alle Entwürfe auswählen"}
            title={alleGewaehlt ? "Auswahl aufheben" : "Alle auswählen"}
          />
        )}
        <span className="pane-title">{gewaehlt.size > 0 ? `${gewaehlt.size} ausgewählt` : "Entwürfe"}</span>
        {drafts && drafts.length > 0 && gewaehlt.size === 0 && <span className="list-anzahl">{drafts.length}</span>}
        <div className="spacer" />
        {gewaehlt.size > 0 && (
          <>
            <button type="button" className="btn quiet" onClick={() => setGewaehlt(new Set())} disabled={loescht}>Abbrechen</button>
            <button type="button" className="btn danger" onClick={() => void auswahlLoeschen()} disabled={loescht}>
              <Trash2 aria-hidden="true" /> {loescht ? "Löscht …" : `${gewaehlt.size} löschen`}
            </button>
          </>
        )}
      </header>
      <div className="pane-scroll">
        <div className="page">
          <p className="lede">
            Vom Agenten vorgeschriebene Antworten und deine eigenen, noch nicht gesendeten Nachrichten.
            Nichts davon ist verschickt — erst dein Klick sendet.
          </p>
          {fehler && !drafts ? (
            <div className="empty" style={{ height: 180 }} role="alert">
              <strong>Entwürfe konnten nicht geladen werden</strong>
              <span>{fehler}</span>
              <button type="button" className="btn" style={{ marginTop: 10 }} onClick={laden}>Erneut versuchen</button>
            </div>
          ) : drafts === null ? (
            <div className="empty" style={{ height: 160 }} aria-busy="true">Lade …</div>
          ) : drafts.length === 0 ? (
            <div className="empty" style={{ height: 180 }}>
              <PenLine className="empty-icon" aria-hidden="true" />
              <strong>Keine Entwürfe</strong>
              <span>Der Agent schreibt vor, wenn eine Mail eine persönliche Antwort erwartet.</span>
            </div>
          ) : (
            <ul className="entwurf-liste">
              {drafts.map((d) => {
                const an = empfaengerVon(d);
                const vorschau = d.body.replace(/\s+/g, " ").trim();
                return (
                  <li key={d.id} className="entwurf" data-gewaehlt={gewaehlt.has(d.id)}>
                    <label className="entwurf-wahl" title="Auswählen (Umschalt-Klick für einen Bereich)">
                      <input
                        type="checkbox"
                        checked={gewaehlt.has(d.id)}
                        onClick={(e) => { e.preventDefault(); umschalten(d.id, e.shiftKey); }}
                        onChange={() => { /* über onClick, damit Umschalt ankommt */ }}
                        aria-label={`Entwurf „${d.subject || "ohne Betreff"}" auswählen`}
                      />
                    </label>
                    <button
                      type="button"
                      className="entwurf-oeffnen"
                      onClick={() => onOpen(d.reply_to_id, d.id, d.reply_to_id ? (d.kind === "neu" ? "antwort" : d.kind) : "neu")}
                    >
                      <span className="entwurf-kopf">
                        <span className="entwurf-betreff">{d.subject || "(kein Betreff)"}</span>
                        {d.source === "llm" && <span className="chip" style={{ ["--chip-color" as string]: "var(--accent)" }}>vorgeschrieben</span>}
                        <time className="msg-date" dateTime={d.created_at} title={datumLang(d.created_at)}>{datumKurz(d.created_at)}</time>
                      </span>
                      <span className="entwurf-an">
                        An {an.length > 0 ? an.join(", ") : "—"}
                        {d.reply_subject ? ` · Antwort auf „${d.reply_subject}"` : ""}
                      </span>
                      {vorschau && <span className="entwurf-vorschau">{vorschau}</span>}
                    </button>
                    <button
                      type="button"
                      className="icon-btn gefahr entwurf-weg"
                      onClick={() => void loeschen(d)}
                      title="Entwurf löschen"
                      aria-label={`Entwurf „${d.subject || "ohne Betreff"}" löschen`}
                    >
                      <Trash2 aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
